import type { BookingCatalog } from "../domain/booking";

export type BookingResource = {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  offerIds: string[];
};

export type BookingSlotResource = {
  id: string;
  resourceId: string;
};

export type BookingPeopleContext = {
  resources: BookingResource[];
  slots: BookingSlotResource[];
};

export const EMPTY_BOOKING_PEOPLE_CONTEXT: BookingPeopleContext = {
  resources: [],
  slots: [],
};

export function bookableResources(catalog: BookingCatalog, context: BookingPeopleContext) {
  const visibleOfferIds = new Set(catalog.offers.map((offer) => offer.id));
  return context.resources.filter((resource) =>
    resource.offerIds.some((offerId) => visibleOfferIds.has(offerId)),
  );
}

export function selectBookingResource(resources: BookingResource[], requestedSlug: string | null) {
  if (requestedSlug) return resources.find((resource) => resource.slug === requestedSlug);
  return resources.length === 1 ? resources[0] : undefined;
}

export function catalogForResource(
  catalog: BookingCatalog,
  context: BookingPeopleContext,
  resourceId: string,
): BookingCatalog {
  const resource = context.resources.find((item) => item.id === resourceId);
  if (!resource) return emptySelectionCatalog(catalog);

  const offerIds = new Set(resource.offerIds);
  const slotResourceIds = new Map(context.slots.map((slot) => [slot.id, slot.resourceId]));
  const offers = catalog.offers.filter((offer) => offerIds.has(offer.id));
  const visibleOfferIds = new Set(offers.map((offer) => offer.id));

  return {
    ...catalog,
    offers,
    variants: catalog.variants.filter((variant) => visibleOfferIds.has(variant.offerId)),
    offerLocations: catalog.offerLocations.filter((link) => visibleOfferIds.has(link.offerId)),
    slots: catalog.slots.filter(
      (slot) =>
        visibleOfferIds.has(slot.offerId) && slotResourceIds.get(slot.id) === resourceId,
    ),
  };
}

export function emptySelectionCatalog(catalog: BookingCatalog): BookingCatalog {
  return {
    ...catalog,
    offers: [],
    variants: [],
    offerLocations: [],
    slots: [],
  };
}

export function firstNameForResource(resource: BookingResource | undefined, fallback = "Rayk") {
  return resource?.name.trim().split(/\s+/)[0] || fallback;
}
